import { ActionIcon, Group, Paper, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { useEffect, useState } from 'react';
import { HiOutlinePlus, HiOutlineTrash } from 'react-icons/hi2';
import ConfirmationModal from '@/components/ConfirmationModal.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import type {
  CompositionMutationOptions,
  CompositionOperation,
} from '@/hooks/api/automations/useCompositionOperations.ts';
import SingleDataInputSelectorInner from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleDataInputSelectorInner.tsx';
import { DatasetType, type AutomationSchemaRef } from '@/types/automationFields.ts';
import { LocalSubflowCallStep } from '@/types/automationSteps.ts';
import {
  AutomationLocalSubflow,
  AutomationLocalSubflowOutputPort,
  AutomationLocalSubflowPort,
} from '@/types/automations.ts';
import {
  DEFAULT_LOCAL_SUBFLOW_SOURCE_OUTPUT_ID,
  emptyLocalSubflowInput,
  localSubflowDatasetOptions,
  newLocalSubflowId,
  outputBindingsFor,
} from './localSubflowEditorUtils.ts';

type InterfacePort = AutomationLocalSubflowPort | AutomationLocalSubflowOutputPort;
type SchemaOption = { value: string; label: string };
type SourceOption = { value: string; label: string };
type RootStepAnchor = {
  step_id: string;
  name: string;
  step_type: string;
};

type PortListProps<TPort extends InterfacePort> = {
  kind: 'input' | 'output';
  ports: TPort[];
  schemas: SchemaOption[];
  sourceSteps?: SourceOption[];
  sourceOutputs?: SourceOption[];
  onChange: (ports: TPort[]) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
};

const schemaValue = (schemaRef: AutomationSchemaRef | null) =>
  schemaRef ? `${schemaRef.schema_id}:${schemaRef.version}` : '__none__';

const parseSchemaRef = (value: string | null): AutomationSchemaRef | null => {
  if (!value || value === '__none__') return null;
  const [schema_id, version] = value.split(':', 2);
  return schema_id && version ? { schema_id, version: Number(version) } : null;
};

const sourceOutputValue = (sourceStepId: string, sourceOutputId: string | null) =>
  `${sourceStepId}:${sourceOutputId ?? DEFAULT_LOCAL_SUBFLOW_SOURCE_OUTPUT_ID}`;

export function PortList<TPort extends InterfacePort>({
  kind,
  ports,
  schemas,
  sourceSteps = [],
  sourceOutputs = [],
  onChange,
  onRemove,
  onAdd,
}: PortListProps<TPort>) {
  const isOutput = kind === 'output';

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text fw={600} size="sm">{isOutput ? 'Outputs' : 'Inputs'}</Text>
        <StyledButton
          size="sm"
          variant="outline"
          disabled={isOutput && sourceSteps.length === 0}
          leftSection={<HiOutlinePlus size={15} />}
          onClick={onAdd}
        >
          Add {kind}
        </StyledButton>
      </Group>
      {ports.map((port, index) => {
        const output = isOutput ? (port as AutomationLocalSubflowOutputPort) : null;
        const updatePort = (update: Partial<TPort>) => {
          onChange(ports.map((item, itemIndex) => (
            itemIndex === index ? { ...item, ...update } : item
          )) as TPort[]);
        };

        return (
          <Paper key={port.port_id} withBorder p="sm">
            <Group align="end">
              <TextInput
                label="Name"
                value={port.name}
                onChange={(event) => updatePort({ name: event.currentTarget.value } as Partial<TPort>)}
                style={{ flex: 1 }}
              />
              <StyledSelect
                label="Dataset"
                data={localSubflowDatasetOptions}
                value={port.dataset_type}
                allowDeselect={false}
                clearable={false}
                onChange={(value) => updatePort({ dataset_type: value as DatasetType } as Partial<TPort>)}
              />
              <StyledSelect
                label="Schema"
                data={schemas}
                value={schemaValue(port.schema_ref)}
                allowDeselect={false}
                clearable={false}
                onChange={(value) => updatePort({ schema_ref: parseSchemaRef(value) } as Partial<TPort>)}
              />
              {output && (
                <>
                  <StyledSelect
                    label="Source step"
                    data={sourceSteps}
                    value={output.source_step_id}
                    allowDeselect={false}
                    clearable={false}
                    onChange={(value) => updatePort({
                      source_step_id: value ?? '',
                      source_output_id: null,
                    } as unknown as Partial<TPort>)}
                  />
                  <StyledSelect
                    label="Source output"
                    data={sourceOutputs.filter((option) => option.value.startsWith(`${output.source_step_id}:`))}
                    value={sourceOutputValue(output.source_step_id, output.source_output_id)}
                    onChange={(value) => {
                      const outputId = value?.split(':', 2)[1];
                      updatePort({
                        source_output_id: outputId === DEFAULT_LOCAL_SUBFLOW_SOURCE_OUTPUT_ID ? null : outputId ?? null,
                      } as unknown as Partial<TPort>);
                    }}
                  />
                </>
              )}
              <Tooltip label={`Remove ${kind}`}>
                <ActionIcon
                  aria-label={`Remove ${port.name || port.port_id}`}
                  color="red"
                  variant="subtle"
                  onClick={() => onRemove(index)}
                >
                  <HiOutlineTrash />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
}

type AttachCallProps = {
  opened: boolean;
  subflow: AutomationLocalSubflow;
  rootSteps: RootStepAnchor[];
  isPending: boolean;
  onClose: () => void;
  onApply: (operations: CompositionOperation[], options?: CompositionMutationOptions) => void;
};

export function AttachCall({
  opened,
  subflow,
  rootSteps,
  isPending,
  onClose,
  onApply,
}: AttachCallProps) {
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [bindings, setBindings] = useState<LocalSubflowCallStep['input_bindings']>([]);
  const options = rootSteps.map((step) => ({
    value: step.step_id,
    label: step.name || step.step_type,
  }));

  useEffect(() => {
    if (!opened) return;
    setAnchorId(rootSteps[0]?.step_id ?? null);
    setBindings(subflow.input_ports.map((port) => ({
      port_id: port.port_id,
      data_input: emptyLocalSubflowInput(port.dataset_type),
    })));
  }, [opened, rootSteps, subflow.input_ports]);

  const targetIndex = anchorId
    ? rootSteps.findIndex((step) => step.step_id === anchorId)
    : 0;

  return (
    <ConfirmationModal
      opened={opened}
      onClose={onClose}
      title={`Add top-level call to ${subflow.name}`}
      message={(
        <Stack gap="sm">
          {options.length > 0 && (
            <StyledSelect
              label="Insert before"
              data={options}
              value={anchorId}
              onChange={setAnchorId}
              allowDeselect={false}
              clearable={false}
            />
          )}
          {subflow.input_ports.map((port) => (
            <SingleDataInputSelectorInner
              key={port.port_id}
              step={{ step_id: anchorId ?? '' } as LocalSubflowCallStep}
              label={port.name || port.port_id}
              dataInput={
                bindings.find((item) => item.port_id === port.port_id)?.data_input
                ?? emptyLocalSubflowInput(port.dataset_type)
              }
              includeEmptyDatasets
              scopeOverride="root"
              onChange={(dataInput) => {
                setBindings((current) => current.map((item) => (
                  item.port_id === port.port_id ? { ...item, data_input: dataInput } : item
                )));
              }}
            />
          ))}
        </Stack>
      )}
      confirmText="Add call"
      confirmType="primary"
      confirmLoading={isPending}
      onConfirm={() => onApply(
        [{
          op_type: 'create_local_subflow_call',
          subflow_id: subflow.subflow_id,
          step_id: newLocalSubflowId('call'),
          subflow_name: subflow.name,
          target_index: Math.max(0, targetIndex),
          value: {
            input_bindings: bindings,
            output_bindings: outputBindingsFor(subflow.output_ports),
          },
        }],
        { onSuccess: onClose },
      )}
    />
  );
}
